import styled from "styled-components";

interface DetailItem {
  label: string;
  value: string | number;
}

interface CoreWidgetProps {
  className?: string;
  statTitle?: string;
  statValue?: string;
  percentageChange?: number | string;
  detailItems: DetailItem[];
}

const CoreWidgetComponent: React.FC<CoreWidgetProps> = ({
  className,
  statTitle,
  statValue,
  percentageChange,
  detailItems,
}) => {
  return (
    <div className={className}>
      <div className="main-content">
        <div className="stat-title">{statTitle}</div>
        <div className="stat-value">
          {statValue}{" "}
          {typeof percentageChange === "number" && (
            <span
              className={`percent-change${
                percentageChange > 0 ? " positive" : " negative"
              }`}
            >
              {percentageChange}%
            </span>
          )}
        </div>
      </div>
      <div className="additional-content">
        {detailItems.map((item, index) => (
          <div key={index} className="detail-item">
            <div className="detail-label">{item.label}</div>
            <div className="detail-value">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CoreWidget = styled(CoreWidgetComponent)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 10px;

  .main-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;
    background-color: ${({ theme }: { theme: any }) =>
      theme.name === "DARK" ? "#2d2d2d" : "#f5f5f5"};
    border-radius: 4px;

    .stat-title {
      font-size: 14px;
      font-weight: 600;
      color: ${({ theme }: { theme: any }) => theme.palette.text.secondary};
      margin-bottom: 10px;
    }

    .stat-value {
      font-size: 18px;
      font-weight: 600;
      color: ${({ theme }: { theme: any }) => theme.palette.text.primary};
    }

    .percent-change {
      font-size: 12px;
      font-weight: 400;

      &.positive {
        color: ${({ theme }: { theme: any }) => theme.palette.success.main};
      }

      &.negative {
        color: ${({ theme }: { theme: any }) => theme.palette.error.main};
      }

      &::before {
        margin-left: 5px;
        margin-right: 2px;
      }

      &.positive::before {
        content: "▲";
      }

      &.negative::before {
        content: "▼";
      }
    }
  }

  .stat-selector {
    position: absolute;
    top: 30px;
    right: 30px;

    svg {
      color: ${({ theme }: { theme: any }) => theme.palette.text.secondary};
    }
  }

  .additional-content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(50px, auto));
    grid-gap: 10px;
    padding: 10px;

    .detail-item {
      .detail-label {
        font-size: 0.75rem;
        font-weight: 300;
        color: ${({ theme }: { theme: any }) => theme.palette.text.secondary};
        cursor: default;
      }

      .detail-value {
        user-select: text;
        font-size: 0.75rem;
        font-weight: 400;
        color: ${({ theme }: { theme: any }) => theme.palette.text.primary};
        cursor: default;

        &.no-cap {
          text-transform: none;
        }
      }
    }
  }
`;

export default CoreWidget;
